import { MEDIA_ACCEPT } from '@lixionary-jira/domain';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import { useIsAdmin, useMe } from '../../../api/session';
import type { TicketDetail } from '../../../api/ticket-types';
import { Badge } from '../../../ui/Badge';
import { Button } from '../../../ui/Button';
import { ErrorAlert, QueryView } from '../../../ui/feedback';
import { Field, Select, Textarea } from '../../../ui/forms';
import { Markdown } from '../../../ui/Markdown';
import { Avatar, Card, LoadMore } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import { FileChip, Files } from './Attachments';
import { DeleteOwn, ReasonDialog } from './dialogs';

interface CommentRow {
  id: string;
  author: { kind: 'human' | 'ai_service' | 'system'; name: string };
  body: string | null;
  createdAt: string;
  editedAt: string | null;
  deleted: { at: string; by: { name: string } | null; reason: string | null } | null;
  version: number;
  revisions: { revision: number; body: string; createdAt: string }[] | number;
  attachments: { id: string; displayId: string; filename: string; scanState: string; sizeBytes: number }[];
  canEdit: boolean;
  canDelete: boolean;
}

export function Discussion({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const q = useInfiniteQuery({
    queryKey: ['comments', t.project.key, t.number],
    queryFn: ({ pageParam }) =>
      api.get<{ items: CommentRow[]; nextCursor: string | null }>(
        `${base}/comments${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  return (
    <>
      <Card
        title="Discussion"
        subtitle="Proposals in comments are not approved scope. Mentions notify people with access."
      >
        <QueryView query={q}>
          {(d) => {
            const items = d.pages.flatMap((p) => p.items);
            return (
              <div>
                {items.length === 0 && <span className="small muted">No comments yet.</span>}
                {items.map((c) => (
                  <CommentItem key={c.id} c={c} t={t} />
                ))}
                <LoadMore
                  hasMore={!!q.hasNextPage}
                  onClick={() => q.fetchNextPage()}
                  loading={q.isFetchingNextPage}
                />
              </div>
            );
          }}
        </QueryView>
        {t.viewer.canComment && <Composer t={t} />}
      </Card>
      <Files t={t} />
    </>
  );
}

function CommentItem({ c, t }: { c: CommentRow; t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const isAdmin = useIsAdmin();
  const me = useMe();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(c.body ?? '');
  const [deleting, setDeleting] = useState(false);
  const inv = [['comments', t.project.key, t.number]];
  const save = useAction(() => api.patch(`${base}/comments/${c.id}`, { body, expectedVersion: c.version }), {
    success: 'Comment updated',
    invalidate: inv,
    onSuccess: () => setEditing(false),
  });
  const moderating = c.canDelete && !c.canEdit && isAdmin;
  return (
    <div className="comment" id={`comment-${c.id}`}>
      <Avatar name={c.author.name} kind={c.author.kind} />
      <div className="stack sm" style={{ minWidth: 0 }}>
        <div className="row small">
          <span className="strong">{c.author.name}</span>
          {c.author.kind === 'ai_service' && (
            <Badge tone="info" dot={false}>
              AI
            </Badge>
          )}
          <span className="muted">
            <Timestamp value={c.createdAt} />
            {c.editedAt && ' · edited'}
          </span>
          {Array.isArray(c.revisions) && c.revisions.length > 1 && (
            <details>
              <summary className="small muted" style={{ cursor: 'pointer' }}>
                {c.revisions.length} revisions
              </summary>
              <ol className="small">
                {c.revisions.map((r) => (
                  <li key={r.revision}>
                    <Timestamp value={r.createdAt} />
                    <Markdown>{r.body}</Markdown>
                  </li>
                ))}
              </ol>
            </details>
          )}
        </div>
        {c.deleted ? (
          <span className="small muted" style={{ fontStyle: 'italic' }}>
            Comment removed by {c.deleted.by?.name ?? 'its author'}
            {c.deleted.reason ? ` — ${c.deleted.reason}` : ''}
          </span>
        ) : editing ? (
          <>
            <Textarea
              aria-label="Edit comment"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <ErrorAlert error={save.error} />
            <div className="row">
              <Button size="sm" onClick={() => save.mutate(undefined)} loading={save.isPending}>
                Save
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <Markdown>{c.body ?? ''}</Markdown>
        )}
        {c.attachments.length > 0 && (
          <div className="row">
            {c.attachments.map((a) => (
              <FileChip key={a.id} t={t} file={a} />
            ))}
          </div>
        )}
        {!c.deleted && !t.project.archived && (c.canEdit || c.canDelete) && (
          <div className="row">
            {c.canEdit && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {c.canDelete && (
              <Button size="sm" variant="ghost" onClick={() => setDeleting(true)}>
                {moderating ? 'Moderate' : 'Delete'}
              </Button>
            )}
          </div>
        )}
      </div>
      {deleting &&
        (moderating ? (
          <ReasonDialog
            title="Remove this comment?"
            description="A tombstone with your name and reason remains; edit history is kept for authorized reviewers."
            confirm="Remove comment"
            danger
            onClose={() => setDeleting(false)}
            run={(reason) => api.del(`${base}/comments/${c.id}?reason=${encodeURIComponent(reason)}`)}
            invalidate={inv}
          />
        ) : (
          <DeleteOwn
            onClose={() => setDeleting(false)}
            run={() => api.del(`${base}/comments/${c.id}`)}
            inv={inv}
            me={me.user.displayName}
          />
        ))}
    </div>
  );
}

function Composer({ t }: { t: TicketDetail }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const people = useQuery({
    queryKey: ['people', t.project.key],
    queryFn: () => api.get<{ id: string; displayName: string }[]>(`/projects/${t.project.key}/people`),
  });
  const post = useAction(
    async () => {
      const c = await api.post<{ id: string }>(`${base}/comments`, { body });
      if (file) {
        const form = new FormData();
        form.append('commentId', c.id);
        form.append('file', file);
        await api.upload(`${base}/attachments`, form);
      }
      return c;
    },
    {
      success: 'Comment posted',
      invalidate: [
        ['comments', t.project.key, t.number],
        ['ticket', t.project.key, t.number],
      ],
      onSuccess: () => {
        setBody('');
        setFile(null);
      },
    },
  );
  const mention = (id: string) => {
    const p = people.data?.find((x) => x.id === id);
    if (!p) return;
    setBody((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}@[${p.displayName}](user:${p.id}) `);
    ref.current?.focus();
  };
  return (
    <form
      className="stack sm"
      style={{ borderTop: '1px solid var(--color-hairline)', paddingTop: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        post.mutate(undefined);
      }}
    >
      <Field label="Add a comment" hint="Markdown supported.">
        {(p) => <Textarea {...p} ref={ref} rows={4} value={body} onChange={(e) => setBody(e.target.value)} />}
      </Field>
      <div className="row">
        <Select
          aria-label="Mention someone"
          value=""
          onChange={(e) => mention(e.target.value)}
          style={{ width: 200, height: 32, fontSize: 13 }}
        >
          <option value="">Mention someone…</option>
          {(people.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </Select>
        <label className="btn btn-secondary sm" style={{ cursor: 'pointer' }}>
          <Paperclip size={14} aria-hidden /> {file ? file.name : 'Attach file'}
          <input
            type="file"
            className="visually-hidden"
            accept={MEDIA_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <span style={{ flex: 1 }} />
        <Button type="submit" loading={post.isPending} disabled={!body.trim()}>
          Comment
        </Button>
      </div>
      <ErrorAlert error={post.error} />
    </form>
  );
}
